import type { LearningResource, PassportCriterion } from "@peer-start/platform-contracts";
import { Disclosure } from "@peer-start/ui";
import { ArrowUpRight, BookOpen, MessageCircle, NotebookPen } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ExtendedLearningLinks } from "./ExtendedLearningLinks.js";
import { LearningVideoStill } from "./LearningVideoStill.js";
import { LearningTextLesson } from "./LearningTextLesson.js";
import { LearningVisualLesson } from "./LearningVisualLesson.js";
import { LearningResourceParts } from "./LearningResourceParts.js";
import type { LearningPresentation } from "../types/learning-presentation.js";

export function LearningResourcePreview({
  resource,
  criteria,
  presentation,
}: {
  resource: LearningResource;
  criteria: PassportCriterion[];
  presentation: LearningPresentation;
}) {
  const { t } = useTranslation();
  const topics = criteria.filter((criterion) => resource.criterionIds.includes(criterion.id)).slice(0, 3);
  if (resource.sections.some((section) => section.video))
    return <LearningResourceParts resource={resource} criteria={criteria} />;
  const focus = t(`passportVig.lesson.focus.${resource.id}`, {
    defaultValue: t("passportVig.lesson.focusFallback", {
      title: resource.title,
      topic: topics[0]?.title ?? resource.title,
    }),
  });
  return (
    <div className="space-y-5">
      {presentation.format === "video" ? (
        <>
          <figure>
            <LearningVideoStill
              key={resource.id}
              imageSrc={presentation.imageSrc}
              resourceId={resource.id}
              title={resource.title}
              vimeoVideos={presentation.vimeoVideos}
            />
            <figcaption className="mt-3 flex items-start gap-2 text-sm text-muted-foreground">
              <BookOpen aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
              {focus}
            </figcaption>
          </figure>
          <ExtendedLearningLinks courses={presentation.extendedCourses} />
          {topics.length ? (
            <section>
              <h4 className="font-medium">{t("passportVig.lesson.topics")}</h4>
              <ul className="mt-3 space-y-2">
                {topics.map((topic) => (
                  <li className="flex items-start gap-2 text-sm" key={topic.id}>
                    <ArrowUpRight aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-primary" />
                    {topic.title}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          <section className="rounded-xl border border-primary/15 bg-primary/5 p-4 sm:p-5">
            <div className="flex items-center gap-2 text-primary">
              <NotebookPen aria-hidden="true" className="size-5" />
              <h4 className="font-medium">{t("passportVig.lesson.reflectionTitle")}</h4>
            </div>
            <p className="mt-2 text-sm leading-relaxed">
              {t("passportVig.lesson.reflectionIntro", { title: resource.title })}
            </p>
            <ol className="mt-4 space-y-3">
              {(["recognise", "consider", "discuss"] as const).map((step, index) => (
                <li className="flex items-start gap-3" key={step}>
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-background text-xs font-medium text-primary">
                    {index + 1}
                  </span>
                  <div className="text-sm">
                    <p className="font-medium">{t(`passportVig.lesson.steps.${step}.title`)}</p>
                    <p className="mt-0.5 text-muted-foreground">
                      {t(`passportVig.lesson.steps.${step}.body`)}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
            <p className="mt-4 flex items-start gap-2 border-t border-primary/10 pt-3 text-xs text-muted-foreground">
              <MessageCircle aria-hidden="true" className="size-4 shrink-0" />
              {t("passportVig.lesson.takeaway")}
            </p>
          </section>
        </>
      ) : presentation.format === "text" ? (
        <LearningTextLesson
          focus={focus}
          title={resource.title}
          topics={topics.map((topic) => topic.title)}
        />
      ) : (
        <LearningVisualLesson
          focus={focus}
          title={resource.title}
          topics={topics.map((topic) => topic.title)}
        />
      )}
      <Disclosure
        className="shadow-none"
        controlId={`passport.learning.${resource.id}.source`}
        title={t("passportVig.lesson.source")}
      >
        <div className="mt-3 space-y-3">
          {resource.sections.map((section) => (
            <section key={section.title}>
              <h5 className="text-sm font-medium">{section.title}</h5>
              <p className="mt-1 text-sm leading-relaxed">{section.body}</p>
              {section.tip ? <p className="mt-1 text-xs text-muted-foreground">{section.tip}</p> : null}
            </section>
          ))}
        </div>
      </Disclosure>
    </div>
  );
}
